/* ============================================================
   WESTSIDE BARBELL: INSTANT SEARCH
   Replaces Boost's instant search. The old custom theme's
   search-input.vue + search-results.vue, in plain JS.

   Attaches to the header's existing search boxes (any input[name=q]
   in a /search form outside <main>), so the header markup is
   untouched. The results panel drops down under the header:

     [ Most popular searches ]  [ Featured products x4 ]     (empty box)
     [ Suggestions           ]  [ Products x4   View All ]   (typing)
     [ Collections / Pages   ]
     [ Articles              ]

   Results come from Shopify's predictive search (/search/suggest.json),
   as before, with the same 200ms debounce and fields (tags included).
   Suggested searches ("query" results) were added on top of the old
   version, since Boost showed them. Enter still submits to the full
   search page.
   ============================================================ */
(function () {
  'use strict';

  var cfgEl = document.getElementById('wsbb-is-config');
  if (!cfgEl || !window.fetch) return;

  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var inputs = Array.prototype.filter.call(
    document.querySelectorAll('form[action$="/search"] input[name="q"]'),
    function (i) { return !i.closest('main'); }
  );
  if (!inputs.length) return;

  var terms = String(cfg.terms || '').split(/[\n,]+/).map(function (t) { return t.trim(); }).filter(Boolean);
  var limit = cfg.limit || 4;
  var SIDE_MAX = 4;   // suggestions / collections / pages / articles each
  var money;
  try {
    money = new Intl.NumberFormat(cfg.locale || undefined, { style: 'currency', currency: cfg.currency || 'USD' });
  } catch (e) {
    money = { format: function (n) { return '$' + Number(n).toFixed(2); } };
  }

  var panel = null;
  var activeInput = null;
  var timer = 0;
  var controller = null;
  var lastQuery = null;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function sized(url, w) {
    if (!url) return '';
    try {
      var u = new URL(url, location.origin);
      u.searchParams.set('width', w);
      return u.toString();
    } catch (e) { return url; }
  }

  /* ---------- Panel ---------- */

  function build() {
    panel = document.createElement('div');
    panel.className = 'wsbb-is';
    panel.id = 'wsbb-is-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Search suggestions and results');
    panel.hidden = true;
    inputs.forEach(function (i) { i.setAttribute('aria-controls', panel.id); });
    panel.innerHTML =
      '<div class="wsbb-is__inner page-width">' +
        '<div class="wsbb-is__side" data-is-side></div>' +
        '<div class="wsbb-is__main" data-is-main></div>' +
      '</div>';
    var overlay = document.createElement('div');
    overlay.className = 'wsbb-is__overlay';
    overlay.hidden = true;
    overlay.addEventListener('click', close);
    panel.overlay = overlay;
    document.body.appendChild(overlay);
    document.body.appendChild(panel);

    panel.addEventListener('click', function (e) {
      var term = e.target.closest('[data-is-term]');
      if (term) {
        e.preventDefault();
        if (activeInput) {
          activeInput.value = term.getAttribute('data-is-term');
          syncClear(activeInput);
          activeInput.focus();
          search(activeInput.value);
        }
        return;
      }
      if (e.target.closest('[data-is-all]')) {
        e.preventDefault();
        if (activeInput && activeInput.form) activeInput.form.submit();
      }
    });
  }

  // Where the visible header ends. Not .header-section's box: it keeps
  // padding reserved for the nav sub-bar even after that bar collapses on
  // scroll, which left a strip of page showing between header and panel.
  // Instead: the header row, plus any sub-bar (desktop nav, mobile search
  // bar) that is actually showing. A collapsed bar slides up behind the
  // header, so its bottom never exceeds the header's and max() ignores it.
  function headerBottom() {
    var wrap = document.querySelector('#HeaderWrapper') || document.querySelector('.site-header');
    var bottom = wrap ? wrap.getBoundingClientRect().bottom : 0;
    document.querySelectorAll('.site-header__element--sub').forEach(function (bar) {
      var r = bar.getBoundingClientRect();
      var cs = getComputedStyle(bar);
      if (r.height > 0 && cs.visibility !== 'hidden' && cs.display !== 'none' && Number(cs.opacity) > 0) {
        bottom = Math.max(bottom, r.bottom);
      }
    });
    return bottom;
  }

  function place() {
    if (!panel) return;
    // Floor, not round: the header's edge often lands on a fraction of a
    // pixel, and rounding down tucks the panel under it rather than leaving
    // a 1px sliver of page showing between them.
    var top = Math.max(0, Math.floor(headerBottom()));
    panel.style.top = top + 'px';
    panel.overlay.style.top = top + 'px';
  }

  function open(input) {
    if (!panel) build();
    activeInput = input;
    place();
    panel.hidden = false;
    panel.overlay.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    if (input.value.trim()) {
      if (input.value.trim() !== lastQuery) search(input.value);
    } else {
      renderEmpty();
    }
  }

  function close() {
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    panel.overlay.hidden = true;
    if (activeInput) activeInput.setAttribute('aria-expanded', 'false');
  }

  /* ---------- Rendering ---------- */

  function productCard(p) {
    var onSale = Number(p.compare) > Number(p.price);
    return '<div class="wsbb-is__product">' +
      '<a class="wsbb-is__img" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' +
        (p.image ? '<img src="' + esc(sized(p.image, 400)) + '" alt="' + esc(p.alt || p.title) + '" loading="lazy" width="400" height="400">' : '') +
      '</a>' +
      '<a class="wsbb-is__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
      '<div class="wsbb-is__price">' +
        (onSale ? '<s>' + esc(money.format(p.compare)) + '</s> ' : '') +
        '<span>' + esc(money.format(p.price)) + '</span>' +
      '</div>' +
    '</div>';
  }

  function productsBlock(heading, list, withAll) {
    return '<p class="wsbb-is__heading">' + esc(heading) + '</p>' +
      '<div class="wsbb-is__products">' + list.slice(0, limit).map(productCard).join('') + '</div>' +
      (withAll ? '<div class="wsbb-is__all"><a href="#" data-is-all>View All</a></div>' : '');
  }

  function renderEmpty() {
    var side = panel.querySelector('[data-is-side]');
    var main = panel.querySelector('[data-is-main]');
    side.innerHTML = terms.length
      ? '<p class="wsbb-is__heading">Most popular searches</p><ul class="wsbb-is__links">' +
        terms.map(function (t) { return '<li><a href="/search?q=' + encodeURIComponent(t) + '" data-is-term="' + esc(t) + '">' + esc(t) + '</a></li>'; }).join('') +
        '</ul>'
      : '';
    main.innerHTML = (cfg.featured || []).length ? productsBlock('Featured products', cfg.featured, false) : '';
    panel.classList.toggle('wsbb-is--no-side', !terms.length);
    if (!side.innerHTML && !main.innerHTML) close();
  }

  // While results load, the panel keeps its finished shape, drawn with the
  // same placeholders as the collection and search pages (wsbb-pf-shell):
  // link rows on the left, product cards (image, title, price) on the right.
  function renderLoading() {
    var side = panel.querySelector('[data-is-side]');
    var main = panel.querySelector('[data-is-main]');
    side.innerHTML = '<div class="wsbb-is__skel-side" aria-hidden="true"><span></span><span></span><span></span><span></span><span></span></div>';
    panel.classList.remove('wsbb-is--no-side');
    var cards = '';
    for (var i = 0; i < limit; i++) {
      cards += '<div class="wsbb-is__product wsbb-is__product--loading" aria-hidden="true">' +
        '<span class="wsbb-is__img"></span><span class="wsbb-is__bar"></span><span class="wsbb-is__bar wsbb-is__bar--short"></span></div>';
    }
    main.innerHTML = '<p class="wsbb-is__heading">Products</p><div class="wsbb-is__products" aria-busy="true">' + cards + '</div>';
  }

  function renderResults(q, r) {
    var side = panel.querySelector('[data-is-side]');
    var main = panel.querySelector('[data-is-main]');

    // Suggested searches first (Shopify's completions and some spelling
    // fixes, as Boost showed), then the old theme's order: collections,
    // pages, articles. Each list is kept short; products get the room.
    var suggestions = (r.queries || []).filter(function (x) { return x.text && x.text.toLowerCase() !== q.toLowerCase(); });
    var sideHTML = suggestions.length
      ? '<p class="wsbb-is__heading">Suggestions</p><ul class="wsbb-is__links">' +
        suggestions.slice(0, SIDE_MAX).map(function (x) {
          return '<li><a href="/search?q=' + encodeURIComponent(x.text) + '" data-is-term="' + esc(x.text) + '">' + esc(x.text) + '</a></li>';
        }).join('') + '</ul>'
      : '';
    var groups = [['Collections', r.collections], ['Pages', r.pages], ['Articles', r.articles]];
    sideHTML += groups.filter(function (g) { return g[1] && g[1].length; }).map(function (g) {
      return '<p class="wsbb-is__heading">' + g[0] + '</p><ul class="wsbb-is__links">' +
        g[1].slice(0, SIDE_MAX).map(function (x) { return '<li><a href="' + esc(x.url) + '">' + esc(x.title) + '</a></li>'; }).join('') +
        '</ul>';
    }).join('');

    // Nothing but products? Fall back to the popular searches, as the old one did.
    if (!sideHTML && terms.length) {
      sideHTML = '<p class="wsbb-is__heading">Most popular searches</p><ul class="wsbb-is__links">' +
        terms.map(function (t) {
          return '<li' + (t.toLowerCase() === q.toLowerCase() ? ' class="is-active"' : '') + '><a href="/search?q=' +
            encodeURIComponent(t) + '" data-is-term="' + esc(t) + '">' + esc(t) + '</a></li>';
        }).join('') + '</ul>';
    }
    side.innerHTML = sideHTML;
    panel.classList.toggle('wsbb-is--no-side', !sideHTML);

    var products = (r.products || []).map(function (p) {
      return {
        title: p.title, url: p.url,
        image: p.featured_image && p.featured_image.url || p.image,
        alt: p.featured_image && p.featured_image.alt,
        price: p.price, compare: p.compare_at_price_min
      };
    });

    if (products.length) {
      main.innerHTML = productsBlock('Products', products, true);
    } else {
      main.innerHTML = '<p class="wsbb-is__none">No product results for ‘' + esc(q) + '’</p>' +
        '<div class="wsbb-is__all"><a href="#" data-is-all>Search articles and pages for ‘' + esc(q) + '’</a></div>';
    }
  }

  /* ---------- Fetching ---------- */

  function suggestParams(q) {
    var params = new URLSearchParams();
    params.set('q', q);
    params.set('resources[type]', 'query,product,article,page,collection');
    // Per type, not in total. Shopify's default limit_scope is "all": the
    // limit is shared across every type, so collections, pages and articles
    // used up the product slots (a search for "belt" showed 2 products).
    // That was the old theme's behavior too, and why search felt strict.
    params.set('resources[limit]', String(Math.max(limit, SIDE_MAX)));
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
    return params.toString();
  }

  // Results for the typed query first, then the synonym's, without repeats.
  function mergeResults(sets) {
    if (sets.length === 1) return sets[0];
    var out = {};
    ['queries', 'products', 'collections', 'pages', 'articles'].forEach(function (type) {
      var seen = {};
      out[type] = [];
      sets.forEach(function (r) {
        (r[type] || []).forEach(function (x) {
          var key = (x.url || x.text || '').toLowerCase();
          if (!key || seen[key]) return;
          seen[key] = true;
          out[type].push(x);
        });
      });
    });
    return out;
  }

  function search(raw) {
    var q = raw.trim();
    lastQuery = q;
    if (!q) { renderEmpty(); return; }
    if (controller) controller.abort();
    controller = window.AbortController ? new AbortController() : null;
    renderLoading();

    // A synonym ("tshirt" for "t-shirt", the Header's "Search synonyms"
    // setting) is searched as well and the two result sets merged: Shopify
    // matches collections by title words, so "tshirt" alone finds no
    // T-Shirts collection.
    var alt = window.wsbbCanonicalQuery ? window.wsbbCanonicalQuery(q) : null;
    var queries = alt ? [q, alt] : [q];
    var opts = controller ? { signal: controller.signal } : {};

    Promise.all(queries.map(function (term) {
      return fetch('/search/suggest.json?' + suggestParams(term), opts)
        .then(function (res) { return res.json(); })
        .then(function (data) { return (data.resources && data.resources.results) || {}; });
    }))
      .then(function (sets) {
        if (q !== lastQuery || !panel || panel.hidden) return;
        renderResults(q, mergeResults(sets));
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (q === lastQuery && panel) renderResults(q, {});
      });
  }

  /* ---------- Wiring ---------- */

  // Show each box's clear (x) only while it has text. Called on typing and
  // whenever the script fills the box itself (a picked suggestion).
  function syncClear(input) {
    var clear = input.parentNode && input.parentNode.querySelector('.wsbb-is__clear');
    if (clear) clear.hidden = !input.value;
  }

  inputs.forEach(function (input) {
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('aria-expanded', 'false');
    // The theme marks the box as a combobox owning a listbox of its own
    // (#predictive-search-results), which this panel replaces and which
    // doesn't exist: a broken reference for screen readers. It stays a
    // combobox, but one that opens this panel, a dialog of links and products
    // (aria-controls is set when the panel is built).
    ['aria-owns', 'aria-controls', 'aria-activedescendant'].forEach(function (a) { input.removeAttribute(a); });
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-haspopup', 'dialog');

    // A clear (x) button inside the box, shown once something is typed (the
    // theme hides the browser's own). Clearing goes back to the panel's
    // popular searches and keeps focus in the box.
    var clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'wsbb-is__clear';
    clear.setAttribute('aria-label', 'Clear search');
    clear.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    input.insertAdjacentElement('afterend', clear);
    input.parentNode.classList.add('wsbb-is__wrap');
    syncClear(input);
    clear.addEventListener('click', function () {
      input.value = '';
      clear.hidden = true;
      input.focus();
      open(input);
      clearTimeout(timer);
      search('');
    });

    input.addEventListener('focus', function () { open(input); });
    input.addEventListener('input', function () {
      syncClear(input);
      if (!panel || panel.hidden) open(input);
      clearTimeout(timer);
      timer = setTimeout(function () { search(input.value); }, 200);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); input.blur(); }
    });
  });

  // Back/forward can restore a box's text without an input event.
  window.addEventListener('pageshow', function () { inputs.forEach(syncClear); });

  // A click outside the panel and the search box closes the panel. Checked
  // against the click's path as dispatched, not e.target's current place: a
  // picked suggestion is replaced by the loading placeholders before this
  // runs, so the clicked link is no longer inside the panel.
  document.addEventListener('click', function (e) {
    if (!panel || panel.hidden) return;
    var path = e.composedPath ? e.composedPath() : [];
    var form = activeInput && activeInput.closest('form');
    if (path.indexOf(panel) > -1 || (form && path.indexOf(form) > -1)) return;
    if (panel.contains(e.target) || (form && form.contains(e.target))) return;
    close();
  });

  window.addEventListener('resize', place);
  // The header animates as it compresses; measure again once it settles.
  document.addEventListener('headerStickyChange', function () {
    if (panel && !panel.hidden) { place(); setTimeout(place, 300); }
  });
  window.addEventListener('scroll', function () { if (panel && !panel.hidden) place(); }, { passive: true });
})();
